import React from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";

import type { NotificationEntity } from "@/types/notifications";
import { notificationsQueryKeys } from "@/hooks/useNotifications";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { notificationsService } from "@/services/notificationsService";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import {
  deriveNotificationEvent,
  isNotificationVisible,
} from "@/lib/notifications";
import { requestKeys } from "@/components/pages/requests/hooks/useRequests";
import { reservationKeys } from "@/hooks/useReservations";
import { settlementKeys } from "@/components/pages/settlement/hooks/useSettlement";
import { roomKeys } from "@/hooks/useRooms";
import { env } from "@/config/runtimeEnv";

const API_URL = env("REACT_APP_API_URL") || "";
const DASHBOARD_QUERY_KEY = ["dashboard"] as const;

export type SSENotification = Pick<
  NotificationEntity,
  | "id"
  | "bookingId"
  | "title"
  | "body"
  | "type"
  | "priority"
  | "createdAt"
  | "metadata"
  | "reference"
>;

type UseNotificationsSSEOptions = {
  disableLocalBuffer?: boolean;
  maxBufferSize?: number;
  debug?: boolean;
  enabled?: boolean;
};

const invalidateDataQueriesForNotification = (
  queryClient: QueryClient,
  notification: Pick<NotificationEntity, "metadata">,
) => {
  const event = deriveNotificationEvent(notification);

  switch (event) {
    case "TASK_CREATED":
    case "TASK_STATUS_UPDATED":
    case "TASK_ASSIGNED":
    case "TASK_TIMEOUT":
      queryClient.invalidateQueries({ queryKey: requestKeys.all });
      queryClient.invalidateQueries({ queryKey: reservationKeys.all });
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      return;
    case "BOOKING_CHECKIN_COMPLETED":
    case "BOOKING_CHECKOUT_UPCOMING":
    case "BOOKING_CHECKOUT_COMPLETED":
    case "TRANSACTION_FAILED":
      queryClient.invalidateQueries({ queryKey: reservationKeys.all });
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
      queryClient.invalidateQueries({ queryKey: roomKeys.all });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
      return;
    default:
      return;
  }
};

export const useNotificationsSSE = (
  options: UseNotificationsSSEOptions = {},
) => {
  const { userProfile } = useAuthenticatedUser();
  const { capabilities } = useAccessCapabilities();

  const queryClient = useQueryClient();

  const [streamIsOpen, setStreamIsOpen] = React.useState(false);
  const [newNotifications, setNewNotifications] = React.useState<
    SSENotification[]
  >([]);

  const maxBufferSize = options.maxBufferSize ?? 50;

  React.useEffect(() => {
    if (options.enabled === false) {
      setStreamIsOpen(false);
      return;
    }

    if (!userProfile?.id) return;

    const url = `${API_URL}/events/${userProfile.id}`;
    const eventSource = new EventSource(url);

    eventSource.onopen = () => {
      if (options.debug) {
        console.warn("NOTIFICATIONS SSE: connection opened");
      }
      setStreamIsOpen(true);
    };

    eventSource.onerror = (error) => {
      if (options.debug) {
        console.error("NOTIFICATIONS SSE: error", error);
      }
      setStreamIsOpen(false);
    };

    const invalidateNotifications = () => {
      queryClient.invalidateQueries({
        queryKey: notificationsQueryKeys.lists(),
      });

      queryClient.invalidateQueries({
        queryKey: notificationsQueryKeys.unreadCount(),
      });
    };

    const handleNotificationEvent = (event: MessageEvent) => {
      if (options.debug) {
        console.warn("NOTIFICATIONS SSE: notification event", event.data);
      }

      const initialNotification = event.data
        ? parseNotificationPayload(event.data, options.debug)
        : null;

      invalidateNotifications();

      if (!event.data) {
        return;
      }

      void handleNotificationPayload(event.data, {
        canUseRequestsSystem: capabilities.canUseRequestsSystem,
        disableLocalBuffer: options.disableLocalBuffer,
        debug: options.debug,
        initialNotification,
        maxBufferSize,
        onInvalidate: (notification) =>
          invalidateDataQueriesForNotification(queryClient, notification),
        onAppend: setNewNotifications,
      });
    };

    eventSource.addEventListener("notification", handleNotificationEvent);
    eventSource.addEventListener("notification_new", handleNotificationEvent);

    return () => {
      eventSource.removeEventListener("notification", handleNotificationEvent);
      eventSource.removeEventListener(
        "notification_new",
        handleNotificationEvent,
      );
      eventSource.close();
    };
  }, [
    maxBufferSize,
    options.debug,
    options.disableLocalBuffer,
    options.enabled,
    capabilities.canUseRequestsSystem,
    queryClient,
    userProfile?.id,
  ]);

  return {
    streamIsOpen,
    newNotifications,
    setNewNotifications,
  };
};

async function handleNotificationPayload(
  eventData: string,
  options: {
    canUseRequestsSystem: boolean;
    disableLocalBuffer?: boolean;
    debug?: boolean;
    initialNotification?: SSENotification | null;
    maxBufferSize: number;
    onInvalidate: (notification: SSENotification) => void;
    onAppend: React.Dispatch<React.SetStateAction<SSENotification[]>>;
  },
) {
  const notification = await resolveNotificationFromPayload(eventData, {
    debug: options.debug,
    initialNotification: options.initialNotification,
  });

  if (!notification) {
    return;
  }

  options.onInvalidate(notification);

  if (options.disableLocalBuffer) {
    return;
  }

  if (!isNotificationVisible(notification, options.canUseRequestsSystem)) {
    return;
  }

  appendNotification(options.onAppend, notification, options.maxBufferSize);
}

async function resolveNotificationFromPayload(
  eventData: string,
  options: {
    debug?: boolean;
    initialNotification?: SSENotification | null;
  },
): Promise<SSENotification | null> {
  const mapped =
    options.initialNotification ??
    parseNotificationPayload(eventData, options.debug);

  if (!mapped) {
    return null;
  }

  if (deriveNotificationEvent(mapped) !== "UNKNOWN") {
    return mapped;
  }

  const resolved = await getFullNotification(mapped.id);
  return resolved ?? mapped;
}

function parseNotificationPayload(
  eventData: string,
  debug?: boolean,
): SSENotification | null {
  try {
    return mapPayload(JSON.parse(eventData));
  } catch (error) {
    if (debug) {
      console.error("NOTIFICATIONS SSE: JSON parse error", error);
    }

    return null;
  }
}

async function getFullNotification(
  id: string,
): Promise<SSENotification | null> {
  try {
    const response = await notificationsService.getNotifications({
      page: 1,
      limit: 20,
    });
    const match = response.data.find((notification) => notification.id === id);

    return match ? toSseNotification(match) : null;
  } catch {
    return null;
  }
}

function toSseNotification(notification: NotificationEntity): SSENotification {
  return {
    id: notification.id,
    bookingId: notification.bookingId ?? null,
    title: notification.title,
    body: notification.body,
    type: notification.type,
    priority: notification.priority,
    createdAt: notification.createdAt,
    metadata: notification.metadata ?? null,
    reference: notification.reference ?? null,
  };
}

function appendNotification(
  onAppend: React.Dispatch<React.SetStateAction<SSENotification[]>>,
  notification: SSENotification,
  maxBufferSize: number,
) {
  onAppend((prev) => {
    const deduped = prev.filter((item) => item.id !== notification.id);
    const next = [...deduped, notification];

    if (next.length > maxBufferSize) {
      return next.slice(next.length - maxBufferSize);
    }

    return next;
  });
}

function mapPayload(payload: unknown): SSENotification | null {
  const raw = getRawPayload(payload);
  const id = getString(raw.id) ?? getString(raw.notification_id);
  const metadata =
    getRecord(raw.metadata) ??
    (getString(raw.event) ? { event: getString(raw.event) } : null);

  if (!id) return null;

  return {
    id,
    bookingId: getString(raw.bookingId) ?? getString(raw.booking_id) ?? null,
    title: getString(raw.title) ?? "",
    body: getString(raw.body) ?? getString(raw.content) ?? "",
    type: getNotificationType(raw.type),
    priority: getNotificationPriority(raw.priority),
    createdAt: getString(raw.createdAt) ?? new Date().toISOString(),
    metadata,
    reference: getString(raw.reference) ?? null,
  };
}

function getRawPayload(payload: unknown): Record<string, unknown> {
  const record = getRecord(payload);
  if (!record) {
    return {};
  }

  return getRecord(record.notification) ?? record;
}

function getRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function getString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function getNotificationType(value: unknown): NotificationEntity["type"] {
  switch (value) {
    case "SERVICE":
    case "MESSAGE":
    case "TASK":
    case "BOOKING":
    case "SYSTEM":
      return value;
    default:
      return "SYSTEM";
  }
}

function getNotificationPriority(
  value: unknown,
): NotificationEntity["priority"] {
  switch (value) {
    case "LOW":
    case "NORMAL":
    case "HIGH":
    case "URGENT":
      return value;
    default:
      return "NORMAL";
  }
}
